/**
 * @file project.ts
 *
 * Client for the commerce-engine `GET /v1/projects` endpoint. Returns the curated,
 * non-secret summary of the authenticated caller's WSB-v2 project — the source of
 * the plan and trial-expiry the admin gates shop access on. Mirrors the backend
 * `ProjectSummary` shape; the project is resolved server-side from the token, so
 * nothing is passed from here.
 *
 * The endpoint is versioned and wraps the payload in the `{ data, meta }`
 * response envelope, so the caller unwraps `.data`.
 */

import { apiFetch } from './client';

/** Success envelope returned by the versioned project endpoint. */
interface ProjectEnvelope {
  data: ProjectSummary;
}

/** Curated project fields surfaced by `GET /v1/projects`. */
export interface ProjectSummary {
  customerContactEmail?: string;
  projectPlan?: string;
  trialExpirationDate?: string;
  userVerified?: boolean;
  domainName?: string;
  generatedDomainName?: string;
}

/**
 * Fetches the authenticated project's summary.
 *
 * @returns The {@link ProjectSummary} for the session's project.
 *
 * @throws {Error} When the request fails (non-2xx).
 */
export async function getProject(): Promise<ProjectSummary> {
  const response = await apiFetch('/v1/projects');
  if (!response.ok) {
    throw new Error(`GET /v1/projects failed: ${response.status}`);
  }
  const envelope = (await response.json()) as ProjectEnvelope;
  return envelope.data;
}
